import React, { SetStateAction } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faRefresh,
	faAdd,
} from '@fortawesome/pro-light-svg-icons';
import { Space, Tag } from 'antd';
import { QueryClient } from 'react-query';
import Button from 'antd/lib/button';
import { Link } from 'react-router-dom';
import {
	CargoContractSides,
	CurrencySymbols,
	DATE,
	EstimateStatus,
	VesselCostType,
	VesselTypes,
} from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { GetEstimateCostsResponse } from '@api/features/estimates/utils/getEstimateCosts';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import VesselSelector from '@client/components/VesselSelector';
import { Links } from '@client/utils/links';
import styles from '../components/styles/EstimateConfigurationCard.module.css';
import {
	OnRefreshEuaPriceMutator,
	OnUpdateEstimate,
} from '../context/hooks/useEstimateHandlers';
import { calcTotal } from '../context/hooks/useMiscEstimateCostHandler';

const estimateConfigurationItems = ({
	estimate,
	vessel,
	onUpdateEstimate,
	onRefreshEuaPriceMutator,
	queryClient,
	setShowFuelEuModal,
	setShowEstimateCostsModal,
	handleMiscCostChange,
	estimateCosts,
	refreshEstimateCosts,
	componentDisabled,
}: {
	estimate: GetEstimateDetailsResponse | null | undefined;
	vessel: GetVesselDetailsResponse | null | undefined;
	onUpdateEstimate: OnUpdateEstimate;
	onRefreshEuaPriceMutator: OnRefreshEuaPriceMutator;
	setShowFuelEuModal: React.Dispatch<SetStateAction<boolean>>;
	setShowEstimateCostsModal: React.Dispatch<SetStateAction<boolean>>;
	queryClient: QueryClient;
	handleMiscCostChange: (value: number) => void;
	estimateCosts: GetEstimateCostsResponse | undefined;
	refreshEstimateCosts: () => Promise<any>;
	hasCargoRelet: boolean;
	componentDisabled?: boolean;
}) => {
	const hasCargoRelet = estimate?.cargos.some(
		(cargo) => cargo.contractSide === CargoContractSides.VC_IN
	);

	const vesselFinanceEntries = vessel?.vesselFinanceEntries || [];
	const thisYear = new Date().getFullYear();
	const isFixed = estimate?.status === EstimateStatus.Fixed;

	const vesselFinanceEntry = vesselFinanceEntries.find((entry) => entry.year === thisYear);
	const financeCost = (vesselFinanceEntry?.capex ?? 0) + (vesselFinanceEntry?.opex ?? 0);
	const hireCost = hasCargoRelet ? 0 : estimate?.tcInRate ?? financeCost;
	const hireAddComm = hasCargoRelet ? 0 : estimate?.hireAddComm ?? 0;

	const euaPrice = (estimate?.euaPriceOverride ?? estimate?.remoteEuaPrice) ?? 0;
	const dwt = (estimate?.dwtOverride ?? vessel?.summerDWT) || undefined;
	const tpc = (estimate?.tpcOverride ?? vessel?.TPC) || undefined;
	const summerDraft = (estimate?.summerDraftOverride ?? vessel?.summerDraft) || undefined;
	const constants = (estimate?.constantsOverride ?? vessel?.constants) || undefined;

	const exchangeRatesAndCurrencies = Array.from(
		new Map(
			estimate?.cargos
				?.filter((cargo) => cargo.currency !== estimate.currency)
				?.map((cargo) => [cargo.currency, {
					exchangeRate: cargo.exchangeRate,
					currency: cargo.currency,
				}]),
		).values(),
	);

	const vesselUnitCostOptions = [
		{
			label: '/day',
			value: VesselCostType.PerDay,
		},
		{
			label: 'Lumpsum',
			value: VesselCostType.Lumpsum,
		},
	];

	const handleVesselCostTypeChange = async (id: number | null) => {
		await onUpdateEstimate({
			vesselId: Number(id),
		});

		if (id != null && (Number(id) < -1)) {
			queryClient.invalidateQueries(['vessels'], { exact: false });
		}
	};

	return [
		{
			label: (
				<Space size={4}>
					<span>Vessel</span>
					{estimate?.isIceClass && <Tag color="blue">Ice class</Tag>}
				</Space>
			),
			span: 1,
			children: (
				<VesselSelector
					disabled={isFixed}
					data-shortcut="vessel"
					placeholder="Select vessel"
					value={estimate?.vesselId}
					includeVesselIndex
					includeMarketVessels
					showNoVessel
					onChange={handleVesselCostTypeChange}
				/>
			),
		},
		...Array.from(exchangeRatesAndCurrencies ?? []).map((item) => {
			return {
				label: `${item.currency} > ${estimate?.currency} exchange rate`,
				span: 1,
				children: item.exchangeRate,
				className: styles.calc,
				disabled: isFixed,
			};
		}),
		{
			label: 'Vessel unit cost',
			span: 1,
			children: (
				<Select
					disabled={isFixed}
					placeholder="Select unit"
					value={estimate?.vesselCostType}
					variant="borderless"
					size="small"
					dropdownStyle={{ width: 350 }}
					optionRender={(option) => (
						<Space size="small">
							{option.label}
						</Space>
					)}
					className={styles.select}
					showSearch
					options={vesselUnitCostOptions}
					onChange={(e) => {
						onUpdateEstimate({
							vesselCostType: String(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Previous contract',
			span: 1,
			className: styles.calc,
			children: estimate?.previousVoyageIdentifier ?
				(
					<Link to={Links.Voyage.get(estimate.previousVoyageId as number)}>
						{estimate!.previousVoyageIdentifier}
					</Link>
				) : '-'
		},
		...(vessel?.type !== VesselTypes.WIND ?
			[{
				label: (<Button onClick={() => setShowFuelEuModal(true)}>FuelEU penalty</Button>),
				span: 1,
				children: (
					<Select
						value={estimate?.isFuelEU ?? true}
						disabled={isFixed || hasCargoRelet}
						variant="borderless"
						size="small"
						dropdownStyle={{ width: 350 }}
						optionRender={(option) => (
							<Space size="small">
								{option.label}
							</Space>
						)}
						className={styles.select}
						options={[
							{ label: 'Yes', value: true },
							{ label: 'No', value: false },
						]}
						onChange={(v) => {
							if (typeof v === 'boolean') {
								onUpdateEstimate({
									isFuelEU: Boolean(v),
								});
							}
						}}
					/>
				),
			}] : []),
		{
			label: 'Hire cost',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed || hasCargoRelet}
					variant="borderless"
					value={hireCost}
					placeholder="Rate"
					addonBefore={CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ??
						estimate?.currency}
					addonAfter={estimate?.vesselCostType === VesselCostType.PerDay ? '/d' : 'LS'}
					onChange={(e) => {
						onUpdateEstimate({
							tcInRate: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Add. comm.',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed || hasCargoRelet}
					variant="borderless"
					value={hireAddComm}
					placeholder=""
					addonAfter="%"
					onChange={(e) => {
						onUpdateEstimate({
							hireAddComm: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'EUA price',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed || hasCargoRelet}
					variant="borderless"
					addonBefore={CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ??
						estimate?.currency}
					value={round(euaPrice, 2)}
					addonAfter={(
						<FontAwesomeIcon
							color={isFixed || hasCargoRelet || componentDisabled ? 'lightgray' : '`#1677ff`'}
							className={!isFixed && !hasCargoRelet && !componentDisabled ? styles.refreshIcon : undefined}
							spin={onRefreshEuaPriceMutator.isLoading}
							icon={faRefresh}
							onClick={() => {
								if (isFixed || hasCargoRelet || componentDisabled) {
									return;
								}

								onRefreshEuaPriceMutator.mutate();
							}}
						/>
					)}
					data-testid="euaPrice"
					placeholder="EUA Cost"
					onChange={(e) => {
						onUpdateEstimate({
							euaPriceOverride: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'UKA price',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed || hasCargoRelet}
					variant="borderless"
					addonBefore={
						CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ??
						estimate?.currency
					}
					value={estimate?.ukaPrice == null ? undefined : round(estimate.ukaPrice, 2)}
					data-testid="ukaPrice"
					placeholder="UKA Price"
					onChange={(e) => {
						onUpdateEstimate({
							ukaPrice: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Open port',
			span: 1,
			children: estimate?.openPort?.name ?? 'N/A',
			className: styles.calc,
		},
		{
			label: 'Open date',
			span: 1,
			children: estimate?.openDate?.format(DATE) ?? 'N/A',
			className: styles.calc,
		},
		{
			label: 'DWT',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed}
					variant="borderless"
					addonAfter="MT"
					value={dwt}
					placeholder="DWT"
					onChange={(e) => {
						onUpdateEstimate({
							dwtOverride: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'TPC',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed}
					variant="borderless"
					value={tpc}
					addonAfter="MT"
					placeholder="TPC"
					onChange={(e) => {
						onUpdateEstimate({
							tpcOverride: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Summer draft',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed}
					variant="borderless"
					addonAfter="m"
					placeholder="Summer draft"
					value={summerDraft}
					onChange={(e) => {
						onUpdateEstimate({
							summerDraftOverride: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Constants',
			span: 1,
			children: (
				<NumericInput
					disabled={isFixed}
					variant="borderless"
					addonAfter="MT"
					value={constants}
					placeholder="Constants"
					onChange={(e) => {
						onUpdateEstimate({
							constantsOverride: Number(e),
						});
					}}
				/>
			),
		},
		{
			label: 'Misc. costs',
			span: 1,
			children: (
				<NumericInput
					disabled={
						isFixed || 
						(estimateCosts && estimateCosts.length > 1) ||
						estimateCosts?.some((e) => e.unitType === VesselCostType.PerDay)
					}
					variant="borderless"
					addonBefore={
						CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ??
						estimate?.currency
					}
					addonAfter={(
						<FontAwesomeIcon
							color={componentDisabled ? 'lightgray' : '#1677ff'}
							className={componentDisabled ? undefined : styles.addIcon}
							icon={faAdd}
							onClick={async () => {
								if (componentDisabled) {
									return;
								}

								await refreshEstimateCosts();
								setShowEstimateCostsModal(true);
							}}
						/>
					)}
					value={
						estimate?.miscCostTotal != null ?
							Number(estimate.miscCostTotal.toFixed(2)) :
							Number(calcTotal(estimateCosts).toFixed(2))
					}
					placeholder="Misc. costs"
					onChange={async (e) => handleMiscCostChange(Number(e))}
				/>
			),
		},
	];
};

export default estimateConfigurationItems;
